"use client";

import { Cross2Icon } from "@radix-ui/react-icons";
import { Table } from "@tanstack/react-table";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { DataTableFacetedFilter } from "./DataTableFacetedFilter";
import { DataTableViewOptions } from "./DataTableViewOptions";
import { Fragment, useState } from "react";
import { useDebounce } from "react-use";

import { Download, Search, TrashIcon, CheckCircle2, Filter, CheckCheck, CheckCircle, Ban } from "lucide-react";
import { useMutation } from "react-query";
import { toast } from "sonner";
import useConfirmModal from "@/hooks/useConfirmModal";
import ConfirmModal from "../modal/ConfirmModal";
import { utils, write } from "xlsx";
import { saveAs } from "file-saver";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  facets;
  onSearch;
  Action?: any;
  onExport;
  isLoading;
  enableExport;
  showSearch?: boolean;
  bulkDelete?: any;
  bulkActivate?: any;
  bulkVerify?: any;
  bulkApprove?: any;
  bulkReject?: any;
  refetch?: any;
}

export function DataTableToolbar<TData>({
  table,
  facets,
  onSearch,
  Action,
  onExport,
  enableExport,
  showSearch = true,
  bulkDelete,
  bulkActivate,
  bulkVerify,
  bulkApprove,
  bulkReject,
  refetch,
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;

  const [search, setsearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const inlineFilters = facets || [];

  useDebounce(
    () => {
      if (showSearch && onSearch) onSearch(search);
    },
    700,
    [search, showSearch]
  );

  const confirmModal = useConfirmModal();

  const selectedRows = table.getSelectedRowModel().rows.map((e) => e.original);

  const deleteAllMutation = useMutation(
    async () => {
      return await bulkDelete(selectedRows);
    },
    {
      onSuccess: () => {
        confirmModal.close();
        refetch();
        table.toggleAllRowsSelected(false);
        toast.success("Deleted successfully");
      },
    }
  );

  const handleExelExport = () => {
    var table = document.getElementById("dataTable");
    var wb = utils.table_to_book(table, { sheet: "Sheet JS" });
    var wbout = write(wb, {
      bookType: "xlsx",
      bookSST: true,
      type: "binary",
    });

    function s2ab(s) {
      var buf = new ArrayBuffer(s.length);
      var view = new Uint8Array(buf);
      for (var i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
      return buf;
    }

    const name = `export-${new Date().toISOString()}.xlsx`;

    saveAs(new Blob([s2ab(wbout)], { type: "application/octet-stream" }), name);
  };

  return (
    <>
      <ConfirmModal
        title={"Are you sure you want to delete?"}
        description={`Lorem ipsum dolor sit amet consectetur adipisicing elit. Excepturi, amet a! Nihil`}
        meta={confirmModal.meta}
        onConfirm={() => deleteAllMutation.mutate()}
        isLoading={deleteAllMutation.isLoading}
        open={confirmModal.isOpen}
        onClose={() => confirmModal.close()}
      />

      <div className="flex flex-col gap-3 px-3 sm:px-4 py-3 border-b bg-white">
        {/* Search, Filter Button, and View - All on one line */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search Bar - Left side */}
          {showSearch && (
          <div className="relative flex-1 min-w-0 sm:w-[360px] sm:max-w-[360px] sm:flex-shrink-0">
            <Input
              placeholder="Search members by name, email, phone..."
              value={search}
              type="search"
              onChange={(event) => setsearch(event.target.value)}
              className="h-9 sm:h-10 w-full pl-9 sm:pl-10 pr-3 sm:pr-4 text-sm bg-white border-gray-300 focus:border-[#0A9F43] focus:ring-1 focus:ring-[#0A9F43] rounded-md shadow-sm transition-all"
            />
            <Search className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          </div>
          )}
          
          {/* Filter Toggle Button - Mobile only, on same line as search */}
          {inlineFilters.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="sm:hidden h-9 px-3 border-gray-300 flex-shrink-0"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
          )}

          {/* Filters - Always visible on desktop, hidden on mobile */}
          <div className="hidden sm:flex flex-wrap items-center gap-2 flex-1 min-w-0">
            {inlineFilters.map((e, i) => (
              <Fragment key={`inline-${i}`}>
                {table.getColumn(e.name) && (
                  <DataTableFacetedFilter
                    column={table.getColumn(e.name)}
                    title={e.title}
                    options={e.options}
                    type={e.type}
                    loader={e.loader}
                    name={e.name}
                    disabled={e.disabled}
                  />
                )}
              </Fragment>
            ))}
          </div>

          {/* View Button - Same line, right side */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {Action && <Action />}
            <DataTableViewOptions table={table} />
          </div>
        </div>
        
        {/* Filters Row - Shown below search on mobile when toggled */}
        {showFilters && inlineFilters.length > 0 && (
          <div className="sm:hidden flex flex-wrap items-center gap-2">
            {inlineFilters.map((e, i) => (
              <Fragment key={`mobile-filter-${i}`}>
                {table.getColumn(e.name) && (
                  <DataTableFacetedFilter
                    column={table.getColumn(e.name)}
                    title={e.title}
                    options={e.options}
                    type={e.type}
                    loader={e.loader}
                    name={e.name}
                    disabled={e.disabled}
                  />
                )}
              </Fragment>
            ))}
          </div>
        )}

        {/* Action Buttons Row - Below search/filters */}
        <div className="flex items-center justify-between gap-3">
          {/* Left side - Reset filters and bulk actions */}
          <div className="flex items-center gap-2 flex-wrap">
            {selectedRows.length > 0 && bulkVerify && (
              <Button
                onClick={async () => {
                  await bulkVerify(selectedRows);
                  table.resetRowSelection();
                }}
                size="sm"
                variant="default"
                className="h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm"
              >
                <CheckCheck className="h-4 w-4 mr-2" />
                Verify {selectedRows.length} {selectedRows.length === 1 ? 'Contribution' : 'Contributions'}
              </Button>
            )}
            {selectedRows.length > 0 && bulkActivate && (
              <Button
                onClick={async () => {
                  if (window.confirm(`Are you sure you want to activate ${selectedRows.length} selected member(s)? This will set their status to Active and ensure they have login credentials.`)) {
                    try {
                      await bulkActivate(selectedRows);
                      table.resetRowSelection();
                      if (refetch) refetch();
                    } catch (error) {
                      console.error("Bulk activate error:", error);
                    }
                  }
                }}
                size="sm"
                variant="default"
                className="h-9 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold shadow-sm"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Activate {selectedRows.length} {selectedRows.length === 1 ? 'Member' : 'Members'}
              </Button>
            )}
            {selectedRows.length > 0 && bulkApprove && (
              <Button
                onClick={async () => {
                  await bulkApprove(selectedRows);
                  table.resetRowSelection();
                }}
                size="sm"
                variant="default"
                className="h-9 px-4 bg-green-700 hover:bg-green-800 text-white font-semibold shadow-sm"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve {selectedRows.length}
              </Button>
            )}
            {selectedRows.length > 0 && bulkReject && (
              <Button
                onClick={async () => {
                  const comment = window.prompt("Rejection reason for all selected");
                  if (comment) {
                    await bulkReject(selectedRows, comment);
                    table.resetRowSelection();
                  }
                }}
                size="sm"
                variant="destructive"
                className="h-9 px-4 font-semibold shadow-sm"
              >
                <Ban className="h-4 w-4 mr-2" />
                Reject {selectedRows.length}
              </Button>
            )}
            {isFiltered && (
              <Button
                variant="ghost"
                onClick={() => table.resetColumnFilters()}
                className="h-8 px-3 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <Cross2Icon className="mr-2 h-3 w-3" />
                Reset Filters
              </Button>
            )}
            {selectedRows.length > 0 && bulkDelete && (
              <Button
                onClick={() => {
                  confirmModal.open({});
                }}
                size="sm"
                variant="destructive"
                className="h-8"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete {selectedRows.length} {selectedRows.length === 1 ? 'row' : 'rows'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
